import { lazy, Suspense, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import useEditablePage from "../../pages/sitePages/useEditablePage";
import HomePageShop from "./homePageShop";
import HomePageRecipes from "./homePageRecipes";
import HOME_DEFAULTS, { newRowKey } from "./homePageDefaults";
import "./bloghomepageoptionsv2.scss";

// Only admins open the editor, so visitors never download it.
const HomePageEditor = lazy(() => import("./homePageEditor"));

// The home page's Shop and Recipes sections, which an admin can edit in place: which products
// are shown, and each recipe card's title, text, image and link.
export default function HomePageDisplay() {
  const { isLoggedIn, currentUserRole } = useAuth();
  const isAdmin = isLoggedIn && currentUserRole === "admin";
  const sectionsRef = useRef(null);

  // The Shop section's product cards (null while loading). They're requested at the same time
  // as the page content rather than after it, and again after each save.
  const [products, setProducts] = useState(null);
  const [productsRequest, setProductsRequest] = useState(0);
  useEffect(() => {
    let cancelled = false;
    axios
      .get("/api/homeProducts")
      .then((response) => {
        if (!cancelled) setProducts(response.data);
      })
      .catch(() => {
        if (!cancelled) setProducts([]); // the section is left out
      });
    return () => {
      cancelled = true;
    };
  }, [productsRequest]);

  const prepareDraft = (content, { isStarter }) => ({
    ...content,
    // Before the first save the section shows the shop's first few products. Starting from
    // those means saving without touching them keeps them.
    productIds:
      isStarter && content.productIds.length === 0
        ? (products ?? []).map((product) => product.shop_item_id)
        : content.productIds,
    recipeCards: content.recipeCards.map((card) => ({ ...card, key: newRowKey() })),
  });

  const { content, isStarter, loadError, draft, setDraft, isSaving, saveError, startEditing, cancelEditing, save } =
    useEditablePage("home", HOME_DEFAULTS, { prepareDraft, scrollToTopOnSave: false });

  const saveAndRefresh = async () => {
    if (!(await save())) return;
    setProductsRequest((count) => count + 1);
    sectionsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="hp-home" ref={sectionsRef}>
      <div className="hp-home-inner">
        {isAdmin && !draft && content && (
          <div className="hp-admin-bar">
            {isStarter && <span>These sections show starter content. Pick your products and recipe cards:</span>}
            <button type="button" className="hp-admin-button" onClick={startEditing}>
              Edit home page
            </button>
          </div>
        )}
        {loadError && isAdmin && <p className="hp-admin-error">{loadError}</p>}

        {draft ? (
          <Suspense fallback={<p className="hp-editor-loading">Loading the editor…</p>}>
            <HomePageEditor
              draft={draft}
              setDraft={setDraft}
              onSave={saveAndRefresh}
              onCancel={cancelEditing}
              isSaving={isSaving}
              error={saveError}
            />
          </Suspense>
        ) : (
          <>
            <HomePageShop
              heading={(content ?? HOME_DEFAULTS).shopHeading}
              products={products}
              isAdmin={isAdmin}
            />
            <HomePageRecipes
              heading={(content ?? HOME_DEFAULTS).recipesHeading}
              cards={content ? content.recipeCards : null}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </div>
  );
}
